// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import type {Gift, GiftDurationType} from '@app/features/gift/commands/GiftCommands';
import type {I18n} from '@lingui/core';
import {msg, plural} from '@lingui/core/macro';

interface GiftDurationTextConfig {
	lifetime: string;
	days: (durationQuantity: number) => string;
	weeks: (durationQuantity: number) => string;
	months: (durationQuantity: number) => string;
	years: (durationQuantity: number) => string;
}
export interface GiftDurationPayload {
	duration_type: GiftDurationType;
	duration_quantity: number;
}
function resolveGiftDuration(gift: Gift | GiftDurationPayload): {
	durationType: GiftDurationType;
	durationQuantity: number;
} {
	return {
		durationType: gift.duration_type,
		durationQuantity: gift.duration_quantity,
	};
}
export function formatGiftDurationText(
	durationType: GiftDurationType,
	durationQuantity: number,
	config: GiftDurationTextConfig,
): string {
	if (durationQuantity === 0) {
		return config.lifetime;
	}
	switch (durationType) {
		case 'days':
			return config.days(durationQuantity);
		case 'weeks':
			return config.weeks(durationQuantity);
		case 'months':
			return config.months(durationQuantity);
		case 'years':
			return config.years(durationQuantity);
		default:
			throw new Error('Unsupported gift duration type');
	}
}
export function getPlutoniumDurationConfig(i18n: I18n): GiftDurationTextConfig {
	return {
		lifetime: i18n._(msg`Visionary (Lifetime ${PREMIUM_PRODUCT_NAME})`),
		days: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Day', other: '# Days'})} of ${PREMIUM_PRODUCT_NAME}`),
		weeks: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Week', other: '# Weeks'})} of ${PREMIUM_PRODUCT_NAME}`),
		months: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Month', other: '# Months'})} of ${PREMIUM_PRODUCT_NAME}`),
		years: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Year', other: '# Years'})} of ${PREMIUM_PRODUCT_NAME}`),
	};
}
export function getPremiumDurationConfig(i18n: I18n): GiftDurationTextConfig {
	return {
		lifetime: i18n._(msg`Lifetime ${PREMIUM_PRODUCT_NAME}`),
		days: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Day', other: '# Days'})} of ${PREMIUM_PRODUCT_NAME}`),
		weeks: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Week', other: '# Weeks'})} of ${PREMIUM_PRODUCT_NAME}`),
		months: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Month', other: '# Months'})} of ${PREMIUM_PRODUCT_NAME}`),
		years: (durationQuantity: number) =>
			i18n._(msg`${plural({count: durationQuantity}, {one: '# Year', other: '# Years'})} of ${PREMIUM_PRODUCT_NAME}`),
	};
}
export function getGiftDurationText(i18n: I18n, gift: Gift | GiftDurationPayload): string {
	const duration = resolveGiftDuration(gift);
	return formatGiftDurationText(duration.durationType, duration.durationQuantity, getPlutoniumDurationConfig(i18n));
}
export function getPremiumGiftDurationText(i18n: I18n, gift: Gift | GiftDurationPayload): string {
	const duration = resolveGiftDuration(gift);
	return formatGiftDurationText(duration.durationType, duration.durationQuantity, getPremiumDurationConfig(i18n));
}
export function extractGiftCode(input: string): string {
	const trimmed = input.trim();
	const lastSlashIndex = trimmed.lastIndexOf('/');
	if (lastSlashIndex === -1) {
		return trimmed;
	}
	return trimmed.slice(lastSlashIndex + 1);
}
